import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthContext } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { trackAuthLinkResend } from "@/lib/analytics-enhanced";
import { describeAuthLinkError, offersFreshLink, readAuthLinkError, withoutAuthLinkError } from "@/lib/auth-link-error";
import {
  describeLinkResend,
  EXPIRED_LINK_PARAM,
  forgetLinkEmail,
  recallLinkEmail,
  sendFreshLink,
} from "@/lib/auth-link-resend";

/**
 * Explains an expired or already-used email link instead of dropping the
 * person on the homepage in silence. See `lib/auth-link-error.ts`.
 *
 * For a stale link it offers a new one: in one tap when this device
 * remembers the address from the sign-up form, else on a one-field form
 * (`lib/auth-link-resend.ts`).
 */
export function AuthLinkErrorHandler() {
  const { user, loading } = useAuthContext();
  const navigate = useNavigate();
  // Read on the first render, before any route has a chance to rewrite the URL.
  const [linkError] = useState(() =>
    typeof window === "undefined" ? null : readAuthLinkError(window.location.href),
  );
  const handled = useRef(false);

  // Mounted on every page, so this is where a signed-in device lets go of
  // the address it kept for a new link.
  useEffect(() => {
    if (user) forgetLinkEmail();
  }, [user]);

  useEffect(() => {
    // Wait for the session: a signed-in person clicking an old link needs no message.
    if (!linkError || handled.current || loading) return;
    handled.current = true;

    // Off the URL, so a reload or a shared link doesn't repeat it.
    navigate(withoutAuthLinkError(window.location.href), { replace: true });
    trackEvent("auth_link_error", { error_code: linkError.code, signed_in: !!user });

    if (user) return;
    const { title, description } = describeAuthLinkError(linkError);
    const signIn = { label: "Sign in", onClick: () => navigate("/auth") };
    if (!offersFreshLink(linkError)) {
      toast.error(title, { description, duration: 20_000, action: signIn });
      return;
    }

    const remembered = recallLinkEmail();
    const sendNewLink = async () => {
      if (!remembered) {
        navigate(`/auth?${EXPIRED_LINK_PARAM}=expired`);
        return;
      }
      const sending = toast.loading("Sending a new link…");
      const outcome = await sendFreshLink(remembered);
      trackAuthLinkResend("toast", outcome.kind);
      const message = describeLinkResend(outcome, remembered);
      if (outcome.kind === "sent" || outcome.kind === "throttled") {
        toast.success(message, { id: sending, duration: 12_000 });
      } else {
        // The address may be wrong or the send refused: the form can take another.
        toast.error(message, {
          id: sending,
          duration: 12_000,
          action: { label: "Try another address", onClick: () => navigate(`/auth?${EXPIRED_LINK_PARAM}=expired`) },
        });
      }
    };
    toast.error(title, {
      description,
      duration: 20_000,
      action: { label: "Email me a new link", onClick: () => void sendNewLink() },
      cancel: signIn,
    });
  }, [linkError, loading, user, navigate]);

  return null;
}
